import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { config } from '../../../config';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const errorParam = params.get('error');
      const errorDescription = params.get('error_description');

      // Check for magic link token
      const token = params.get('token');
      const type = params.get('type');

      // Handle errors
      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }

      try {
        // Magic link verification
        if (token && type === 'magic_link') {
          const response = await fetch(
            `${config.apiUrl}/auth/magic-link/verify?token=${encodeURIComponent(token)}`,
            {
              credentials: 'include',
              redirect: 'manual',
            }
          );

          // The verify endpoint redirects on success
          if (response.type === 'opaqueredirect' || response.ok) {
            navigate({ to: '/' });
            return;
          } else {
            const data = await response.json();
            setError(data.error || 'Magic link verification failed');
            return;
          }
        }

        // No valid auth data found
        setError('No authorization data provided');
      } catch {
        setError('Failed to complete authentication');
      }
    };

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-red-500 mb-4">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate({ to: '/login', search: {} })}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Completing sign in...</p>
        </div>
      </div>
    </div>
  );
}
